import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTikTokAdsAuthUrl, useTikTokAdsConnectionStatus } from "@/api/tiktok-ads";
import { useTikTokShopAuthUrl, useTikTokShopConnectionStatus } from "@/api/tiktok-shop";
import { useMerchant } from "@/providers/MerchantProvider";
import type { ConnectionRowState } from "./connection-row";

/**
 * Shared TikTok Shop/Ads connection state + connect handlers, used by both the
 * TikTokSellerConnectionsModal and the Integrations page so their button behavior
 * (and the underlying queries) never drift apart.
 */
export const useTikTokSellerConnections = (enabled: boolean) => {
  const merchant = useMerchant();
  const navigate = useNavigate();

  const [shopRedirecting, setShopRedirecting] = useState(false);
  const [adsRedirecting, setAdsRedirecting] = useState(false);

  const { data: shopAuthData, isLoading: shopAuthLoading } = useTikTokShopAuthUrl(enabled);
  const { data: shopStatus, isLoading: shopStatusLoading } = useTikTokShopConnectionStatus();

  const { data: adsAuthData, isLoading: adsAuthLoading } = useTikTokAdsAuthUrl(enabled);
  const { data: adsStatus, isLoading: adsStatusLoading } = useTikTokAdsConnectionStatus(enabled, {
    refetchInterval: adsRedirecting ? 2000 : false,
    stats: true,
  });

  // Stop polling when ads connection succeeds
  const adsConnected = adsStatus?.state === "connected";

  useEffect(() => {
    if (adsRedirecting && adsConnected) {
      setAdsRedirecting(false);
    }
  }, [adsRedirecting, adsConnected]);

  const handleShopConnect = () => {
    if (!merchant) {
      toast.error("No merchant selected. Please select a merchant first.");
      return;
    }
    if (!shopAuthData?.authUrl) {
      toast.error("TikTok Shop API is not configured. Please contact support.");
      return;
    }
    setShopRedirecting(true);
    window.location.href = shopAuthData.authUrl;
  };

  const handleAdsConnect = () => {
    if (!merchant) {
      toast.error("No merchant selected. Please select a merchant first.");
      return;
    }
    if (!adsAuthData?.authUrl) {
      toast.error("TikTok Ads API is not configured. Please contact support.");
      return;
    }

    const target = new URL(adsAuthData.authUrl, window.location.origin);
    if (target.origin === window.location.origin) {
      navigate({ to: "/connect/ads", search: { merchant_id: target.searchParams.get("merchant_id") ?? undefined } });
      return;
    }

    setAdsRedirecting(true);
    window.location.href = adsAuthData.authUrl;
  };

  const shopState: ConnectionRowState = shopStatus?.connected
    ? "connected"
    : shopRedirecting
      ? "redirecting"
      : "disconnected";

  const adsState: ConnectionRowState =
    adsRedirecting && !adsConnected ? "redirecting" : (adsStatus?.state ?? "disconnected");

  return {
    shopStatus,
    shopState,
    isShopLoading: shopAuthLoading || shopStatusLoading,
    shopAuthUrl: shopAuthData?.authUrl,
    handleShopConnect,
    adsStatus,
    adsState,
    isAdsLoading: adsAuthLoading || adsStatusLoading,
    adsAuthUrl: adsAuthData?.authUrl,
    adsManageUrl: adsAuthData?.consentUrl,
    handleAdsConnect,
  };
};
